import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faX } from '@fortawesome/free-solid-svg-icons';
import { withAppHOC } from '../../hoc';
import {
  StyledContentContainer,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledStatusPill,
  StyledFormContainer,
  StyledModalContainer,
} from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import {
  Button,
  Dropdown,
  Fieldset,
  Form,
  Label,
  MessageBox,
  TextInput,
} from '../../components/FormElements';
import Modal from '../../components/Modal';
import Loader from '../../components/Loader/Loader';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../components/NoData/NoData';
import { TableBuilder } from '../../components/TableElements';
import Pagination from '../../components/Pagination/Pagination';
import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import { formatCurrency, getSearchParams } from '../../utils';
import FiltersButton from '../../components/FiltersButton/FiltersButton';

const PaymentGatewayDeposits = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    fStatus: '',
    fAmount: '',
    fStartDate: '',
    fEndDate: '',
    fUpiTransactionId: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 30,
  });
  const [apiState, setApiState] = useState({
    isLoading: false,
    apiStatus: '',
    apiMessage: '',
    showModal: false,
    amount: '',
    remark: '',
  });

  const loadWallet = () => {
    setApiState((prevState) => ({
      ...prevState,
      isLoading: false,
      apiStatus: '',
      apiMessage: '',
      amount: '',
      remark: '',
      showModal: true,
    }));
  };

  const createPaymentGatewayRequest = async (e) => {
    e?.preventDefault();
    setApiState((prevState) => ({
      ...prevState,
      isLoading: true,
      apiStatus: '',
      apiMessage: '',
    }));

    const response = await API.post(`/payments/payment-gateway-deposits`, {
      amount: apiState.amount,
      remark: apiState.remark,
    });
    console.log(response.data);

    if (response.data?.status === RESPONSE_STATUSES.FAILED) {
      setApiState((prevState) => ({
        ...prevState,
        apiStatus: RESPONSE_STATUSES.FAILED,
        apiMessage: response.data?.message,
      }));
    } else {
      if (response.data?.data?.url) {
        window.location.href = response.data?.data?.url;
      }
    }
    setApiState((prevState) => ({
      ...prevState,
      isLoading: false,
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const readData = async () => {
    try {
      const response = await API.get(
        `/payments/payment-gateway-deposits?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fStatus=${appliedFilters.fStatus}&fAmount=${appliedFilters.fAmount}&fStartDate=${appliedFilters.fStartDate}&fEndDate=${appliedFilters.fEndDate}&fUpiTransactionId=${appliedFilters.fUpiTransactionId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const exportData = async () => {
    try {
      const response = await API.get(
        `/payments/payment-gateway-deposits-export?fStatus=${appliedFilters.fStatus}&fAmount=${appliedFilters.fAmount}&fStartDate=${appliedFilters.fStartDate}&fEndDate=${appliedFilters.fEndDate}&fUpiTransactionId=${appliedFilters.fUpiTransactionId}`
      );
      setApiState({
        apiStatus: response?.data?.status ?? '',
        apiMessage: response?.data?.message ?? '',
      });
      setTimeout(() => {
        setApiState({
          apiStatus: '',
          apiMessage: '',
        });
      }, 3000);
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['payment_gateway_deposits', paginationData, appliedFilters],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      fStatus: '',
      fAmount: '',
      fStartDate: '',
      fEndDate: '',
      fUpiTransactionId: '',
    });
    setAppliedFilters({
      fStatus: '',
      fAmount: '',
      fStartDate: '',
      fEndDate: '',
      fUpiTransactionId: '',
    });
  };

  const renderPageHeading = () => (
    <PageHeader
      title="Payment Gateway Deposits"
      showReloadData={true}
      reloadData={reloadData}
      showLoadButton={true}
      onClickLoad={() => loadWallet()}
      showExportButton={true}
      onClickExport={exportData}
    />
  );

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        {apiState?.apiStatus && apiState?.apiMessage && !apiState.showModal && (
          <MessageBox
            status={apiState?.apiStatus}
            message={apiState?.apiMessage}
            style={{ marginBottom: 20 }}
          />
        )}
        <StyledFiltersContainer className={showFilters ? 'show' : ''}>
          <StyledFilters>
            <StyledFilterContainer>
              <Dropdown
                value={filters.fStatus}
                onChange={(value) => handleFilterChange('fStatus', value)}
                placeholder="Status"
                disabled={isLoading}
                options={[
                  {
                    name: '-- SELECT STATUS --',
                    value: '',
                  },
                  {
                    name: 'All',
                    value: 'All',
                  },
                  {
                    name: 'Pending',
                    value: 'Pending',
                  },
                  {
                    name: 'Success',
                    value: 'Success',
                  },
                  {
                    name: 'Failure',
                    value: 'Failure',
                  },
                ]}
              />
              <TextInput
                value={filters.fAmount}
                onChange={(value) => handleFilterChange('fAmount', value)}
                placeholder="Amount"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fStartDate}
                onChange={(value) => handleFilterChange('fStartDate', value)}
                placeholder="From Date"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fEndDate}
                onChange={(value) => handleFilterChange('fEndDate', value)}
                placeholder="To Date"
                disabled={isLoading}
              />
              <TextInput
                value={filters.fUpiTransactionId}
                onChange={(value) =>
                  handleFilterChange('fUpiTransactionId', value)
                }
                placeholder="UTR Number"
                disabled={isLoading}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: 'Requested On',
                  dataSelector: 'createdOn',
                  dataType: 'date',
                  align: 'center',
                },
                {
                  title: 'Updated On',
                  dataSelector: 'updatedOn',
                  dataType: 'string',
                  cellrenderer: (value) => (value ? value : 'N/A'),
                },
                {
                  title: 'Amount',
                  dataSelector: 'amount',
                  dataType: 'number',
                  align: 'center',
                  cellrenderer: (value) => formatCurrency(value),
                },
                {
                  title: 'UTR Number',
                  dataSelector: 'utrNumber',
                  dataType: 'string',
                  cellrenderer: (value) => (value ? value : 'N/A'),
                },
                {
                  title: 'Remark',
                  dataSelector: 'description',
                  dataType: 'string',
                  cellrenderer: (value) => (value ? value : 'N/A'),
                },
                {
                  title: 'Transaction Status',
                  dataSelector: 'transactionsStatus',
                  dataType: 'string',
                  align: 'center',
                  cellrenderer: (value) =>
                    value && (
                      <StyledStatusPill
                        className={
                          value === 'Success'
                            ? 'green'
                            : value === 'Failure'
                            ? 'red'
                            : value === 'Pending'
                            ? 'blue'
                            : 'yellow'
                        }
                      >
                        {value}
                      </StyledStatusPill>
                    ),
                },
                {
                  title: 'Gateway Type',
                  dataSelector: 'gatewayType',
                  dataType: 'string',
                  cellrenderer: (value) => (value ? value : 'N/A'),
                },
                {
                  title: 'Remark',
                  dataSelector: 'rejectReason',
                  dataType: 'string',
                  cellrenderer: (value) => (value ? value : 'N/A'),
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
          </>
        )}
        <Modal
          modalStatus={apiState.showModal}
          setModalStatus={() => {
            setApiState((prevState) => ({
              ...prevState,
              isLoading: false,
              apiStatus: '',
              apiMessage: '',
              showModal: false,
              amount: '',
              remark: '',
            }));
          }}
          hideCloseButton
        >
          <StyledModalContainer width={`500px`}>
            <h3>Load Wallet Balance</h3>
            <StyledFormContainer>
              <Form
                method="POST"
                action="#"
                onSubmit={createPaymentGatewayRequest}
              >
                <Fieldset>
                  <Label required>Amount</Label>
                  <TextInput
                    isNumeric
                    value={apiState.amount}
                    onChange={(value) => {
                      setApiState((prevState) => ({
                        ...prevState,
                        amount: value,
                      }));
                    }}
                    placeholder="Please enter amount"
                    disabled={apiState.isLoading}
                  />
                </Fieldset>
                <Fieldset>
                  <Label required>Remark</Label>
                  <TextInput
                    value={apiState.remark}
                    onChange={(value) => {
                      setApiState((prevState) => ({
                        ...prevState,
                        remark: value,
                      }));
                    }}
                    placeholder="Please enter remark"
                    disabled={apiState.isLoading}
                  />
                </Fieldset>
                {apiState.showModal &&
                  apiState.apiStatus &&
                  apiState.apiMessage && (
                    <Fieldset>
                      <MessageBox
                        status={apiState.apiStatus}
                        message={apiState.apiMessage}
                      />
                    </Fieldset>
                  )}
                <Fieldset>
                  <Button
                    disabled={apiState.isLoading}
                    type="submit"
                    style={{ width: 'auto' }}
                  >
                    Add
                  </Button>
                </Fieldset>
                <Loader isLoading={apiState.isLoading} />
              </Form>
            </StyledFormContainer>
          </StyledModalContainer>
        </Modal>
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(PaymentGatewayDeposits);
